import { useMemo } from "react";

import {
  VisionBenefits,
  GeneralInformation,
  PlanRates,
  VisionCoverage,
  VisionPlan,
} from "../../constants/aquafina";
import CollapsableSection from "../sections/collapsable-section";
import { OutOfNetworkDefaultValue, RangeIterator } from "../../utils";
import ContributionType from "../sections/contribution-type-section";
import Rates from "../rates";
import {
  CoverageDividerRow,
  CoverageRow,
  CoverageHeaderRow,
  NotProvidedRow,
} from "../table/rows";
import {
  CoverageVectorCell,
  CoverageVectorHeaderCell,
  CoverageCell,
} from "../table/cells";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

type Props = {
  viewMode: ViewMode;
  coverage: VisionCoverage;
  displayV4Fields: boolean;
};

function Vision({ coverage, viewMode, displayV4Fields }: Props) {
  const state = useMemo(() => {
    const benefitDetails: VisionBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const fundingTypes: (string | null)[] = [];
    const rates: PlanRates[] = [];
    const plans: VisionPlan[] = [];
    let inconsistentRateData = false;

    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      fundingTypes.push(plan.generalInformation?.fundingType);
      rates.push(plan.planRates);
      plans.push(plan);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });

    return {
      benefitDetails,
      generalInfo,
      fundingTypes,
      plans,
      rates,
      inconsistentRateData,
    };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo data={state.generalInfo} showFundingType />
      )}
      <VisionBenefitDetails
        benefits={state.benefitDetails}
        fundingTypes={state.fundingTypes}
        displayV4Fields={displayV4Fields}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
}

type VisionBenefitDetailsProps = {
  benefits: VisionBenefits[];
  fundingTypes: (string | null)[];
  displayV4Fields: boolean;
};

const VisionBenefitDetails = ({
  benefits,
  fundingTypes,
  displayV4Fields,
}: VisionBenefitDetailsProps) => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <VisionBenefitDetailsData
      fundingTypes={fundingTypes}
      benefits={benefits}
      displayV4Fields={displayV4Fields}
    />
  </CollapsableSection>
);

const VisionBenefitDetailsData = ({
  benefits,
  fundingTypes,
  displayV4Fields,
}: VisionBenefitDetailsProps) => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorHeaderCell
            label={["In Network", "Out of Network"]}
            key={index}
          />
        ))}
      </CoverageHeaderRow>
      <CoverageDividerRow label="Plan Details" totalPlans={benefits.length} />
      <CoverageRow label="Exam" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.exam,
              benefits[index].outOfNetwork?.exam ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Materials Copay">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.materials,
              benefits[index].outOfNetwork?.materials ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Single Lenses" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.singleLenses,
              benefits[index].outOfNetwork?.singleLenses ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Bifocal Lenses">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.bifocalLenses,
              benefits[index].outOfNetwork?.bifocalLenses ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Trifocal Lenses" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.trifocalLenses,
              benefits[index].outOfNetwork?.trifocalLenses ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Lenticular Lenses">
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={[
                  benefits[index].inNetwork?.lenticularLenses,
                  benefits[index].outOfNetwork?.lenticularLenses ||
                    OutOfNetworkDefaultValue(
                      benefits[index].outOfNetwork,
                      fundingTypes[index],
                    ),
                ]}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Standard Progressive" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={[
                  benefits[index].inNetwork?.standardProgressive,
                  benefits[index].outOfNetwork?.standardProgressive ||
                    OutOfNetworkDefaultValue(
                      benefits[index].outOfNetwork,
                      fundingTypes[index],
                    ),
                ]}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
      <CoverageRow label="Standard Frames">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.frames,
              benefits[index].outOfNetwork?.frames ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <CoverageRow label="Additional Discounts on Frames" isWhite>
          {RangeIterator(benefits.length, (index) => (
            <CoverageVectorCell
              value={[
                benefits[index].inNetwork?.framesDiscounts,
                benefits[index].outOfNetwork?.framesDiscounts ||
                  OutOfNetworkDefaultValue(
                    benefits[index].outOfNetwork,
                    fundingTypes[index],
                  ),
              ]}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
      <CoverageRow label="Contacts - Elective" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.contactsElective,
              benefits[index].outOfNetwork?.contactsElective ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow
        label="Contacts - Medically Necessary"
        isWhite={displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.contactsMedicallyNecessary,
              benefits[index].outOfNetwork?.contactsMedicallyNecessary ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Contact Lens Exam" isWhite={!displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={[
                  benefits[index].inNetwork?.contactLensExam,
                  benefits[index].outOfNetwork?.contactLensExam ||
                    OutOfNetworkDefaultValue(
                      benefits[index].outOfNetwork,
                      fundingTypes[index],
                    ),
                ]}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Annual Open Enrollment" isWhite={displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].annualOpenEnrollment}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
      <CoverageDividerRow
        label="Frequencies"
        totalPlans={benefits.length}
        isWhite={!displayV4Fields}
      />
      <CoverageRow label="Exam" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].examFrequency} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Lenses" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].lensesFrequency} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Frames" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].framesFrequency} key={index} />
        ))}
      </CoverageRow>
    </>
  );
};

export default Vision;
